import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Seo from "../components/Seo";
import SectionEyebrow from "../components/SectionEyebrow";
import RevealOnScroll from "../components/RevealOnScroll";
import Button from "../components/Button";
import Lightbox from "../components/Lightbox";
import WistiaPlayer from "../components/WistiaPlayer";
import CaseStudyHero from "../components/CaseStudyHero";
import { WORKS } from "../data/content";
import { breadcrumbSchema } from "../lib/schemas";

const SchedaRow = ({ label, children }) => (
  <div className="grid grid-cols-12 gap-3 py-3 border-b border-current/15 text-[14px] leading-[1.55]">
    <dt className="col-span-5 md:col-span-4 font-condensed uppercase tracking-widest text-[12px] md:text-[13px] opacity-70">
      {label}
    </dt>
    <dd className="col-span-7 md:col-span-8">{children}</dd>
  </div>
);

const ApproccioBlock = ({ num, title, body }) => (
  <div className="border border-galactico/15 p-7 md:p-9 bg-galactico/[0.03]">
    <SectionEyebrow num={num}>{title}</SectionEyebrow>
    <p className="mt-5 text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[48ch]">
      {body}
    </p>
  </div>
);

const KpiBlock = ({ value, label }) => (
  <div className="border-t border-galactico/25 pt-6">
    <div
      className="font-display leading-[0.92] tracking-tight text-galactico"
      style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}
    >
      {value}
    </div>
    <div className="font-condensed uppercase tracking-widest text-[12px] md:text-[13px] mt-4 opacity-85 max-w-[24ch] leading-[1.35]">
      {label}
    </div>
  </div>
);

// Galleria 12 slot, mapping 1:1 stretto (slot i ↔ GALLERY_PHOTOS[i]).
// Slot oltre la lunghezza dell'array mostrano placeholder "Foto in arrivo".
// L'utente caricherà le foto in batch in /public/images/casi-studio/mizuno/01.jpg ... 12.jpg.
// Per ora nessuna foto è disponibile → tutti placeholder.
const GALLERY_SLOTS = 12;
const GALLERY_PHOTOS = [
  // 01 — Group shot dei 4 azzurri testimonial (Galassi, Romanò, Recine, Scanferla) con scarpe in mano
  "/images/assets/ccxit9ac_01---mizuno.jpg",
  // 02 — Ritratto verticale full-body con scarpa appoggiata sulla spalla, sfondo drappo blu
  "/images/assets/wup9rt7v_20221010_10-20-05_z61_5403-min-768x1024.jpg",
  // 03 — Ritratto seduto dal basso, palla Mizuno e ginocchiera Mizuno in evidenza
  "/images/assets/lj28jye6_20221010_10-10-41_z61_5364-min-768x1024.jpg",
  // 04 — Ritratto seduto con scarpa Mizuno su piano laterale, posa contemplativa
  "/images/assets/40p5emm4_20221010_09-22-18_z61_5221-min-768x1024.jpg",
  // 05 — Still life prodotto: scarpa da volley Mizuno blu su cubo blu, illuminazione editoriale
  "/images/assets/k1su289p_20221010_09-02-50_z61_5205-scaled.jpg",
  // 06 — Ritratto editoriale con hoodie Mizuno blu, scarpe in mano, fondale dipinto napoleonico
  "/images/assets/ygbb9z4r_20221010_15-06-34_z62_8555-min-1-980x1307.jpg",
  // 07 — Ritratto seduto frontale con hoodie Mizuno e scarpa Wave Lightning, sfondo dipinto storico
  "/images/assets/h3s94rcx_20221010_15-02-03_z62_8537-min-1-768x1024.jpg",
  // 08 — Still life prodotto: coppia scarpe Mizuno blu su pavimento a mosaico e pietra
  "/images/assets/kvwav586_20221010_15-15-14_z62_8557-min-980x1307.jpg",
  // 09 — Ritratto con hoodie Mizuno blu e scarpe in mano, fondale dipinto di scena di battaglia
  "/images/assets/qqc33cyv_20221010_14-57-38_z62_8523-min-2-768x1024.jpg",
  // 10 — Primo piano editoriale: atleta con scarpe Mizuno in mano davanti al viso, drappo blu
  "/images/assets/30qk5a2s_20221010_09-57-13_z61_5296-min-980x1307.jpg",
  // 11 — Ritratto laterale con hoodie Mizuno blu e scarpa Wave Lightning, sfondo dipinto storico
  "/images/assets/hrbjyqpj_20221010_14-17-32_z62_8384-min-768x1024.jpg",
  // 12 — Still life prodotto: coppia scarpe Mizuno blu appoggiate a colonna classica, pavimento marmoreo
  "/images/assets/7spbmqh1_20221010_15-50-39_z62_8644-min-1-980x1307.jpg",
];
const LIGHTBOX_IMAGES = GALLERY_PHOTOS.map((src, i) => ({
  src,
  alt: `Mizuno × Reach Beyond — Asset ${String(i + 1).padStart(2, "0")} di ${GALLERY_SLOTS}`,
}));

const WorkMizunoReachBeyond = () => {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Lock body scroll quando il lightbox è aperto
  useEffect(() => {
    if (lightboxIndex !== null) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [lightboxIndex]);

  const idx = WORKS.findIndex((w) => w.to === "/works/mizuno-reach-beyond");
  const prev = idx > 0 ? WORKS[idx - 1] : WORKS[WORKS.length - 1];
  const next = idx >= 0 ? WORKS[(idx + 1) % WORKS.length] : WORKS[0];

  const breadcrumbJsonLd = breadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Works", url: "/works" },
    { name: "Mizuno × Reach Beyond World Champions", url: "/works/mizuno-reach-beyond" },
  ]);

  return (
    <>
      <Seo
        title="Mizuno × 4 azzurri pallavolo Mondiali 2022 | OnSports Case"
        description="Campagna Mizuno Reach Beyond World Champions con Galassi, Romanò, Recine, Scanferla. Attivazione sponsorship sugli azzurri della pallavolo. OnSports 2023."
        path="/works/mizuno-reach-beyond"
        image="/images/assets/ccxit9ac_01---mizuno.jpg"
        jsonLd={breadcrumbJsonLd}
      />

      {/* SEZ. 1 — HERO */}
      <CaseStudyHero
        imagePath="/images/works/mizuno-reach-beyond/hero.gif"
        imageAlt="Mizuno · Reach Beyond"
        eyebrow="● Case study · Business Impact · Content · Performance"
        titleLines={["Dal training", "alla CONVERSIONE."]}
        highlightWord="CONVERSIONE."
        subtitle="Abbiamo costruito il lancio italiano della scarpa da volley Mizuno come un sistema integrato: concept creativo, quattro azzurri Campioni del Mondo come testimonial, video premium, sales page dedicata e performance marketing con ROI tracciabile. Awareness e conversione in un solo funnel."
        meta="2023  ·  Cliente — Mizuno  ·  Categoria — Content & Performance"
      />

      {/* SEZ. 2 — OVERVIEW */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-overview">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-7">
              <RevealOnScroll>
                <SectionEyebrow>Overview</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Il progetto.</h2>
              </RevealOnScroll>
              <div className="mt-8 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[760px]">
                <RevealOnScroll delay={0.15}>
                  <p>
                    Mizuno è uno dei brand storici della pallavolo mondiale. La
                    sua linea di scarpe da volley è da sempre tra i prodotti di
                    riferimento per gli atleti professionisti — ma il pubblico
                    italiano del volley è cresciuto enormemente negli ultimi
                    anni, soprattutto sull'onda dei successi delle nazionali
                    maggiori, e con esso si è ampliata una platea di
                    appassionati e praticanti amatoriali che oggi acquista
                    prodotti tecnici online con criteri sempre più consapevoli.
                    Mizuno ci ha coinvolti per costruire il lancio italiano di
                    una nuova scarpa da volley pensando direttamente a questo
                    pubblico.
                  </p>
                </RevealOnScroll>
                <RevealOnScroll delay={0.2}>
                  <p>
                    Abbiamo proposto un sistema integrato che partisse da un
                    concept editoriale forte — Reach Beyond — costruito attorno
                    ai quattro azzurri testimonial Mizuno: Gianluca Galassi,
                    Yuri Romanò, Francesco Recine, Leonardo Scanferla. Campioni
                    del Mondo, tutti accomunati dall'idea che il segreto per
                    vincere è diventare campioni nell'allenamento prima ancora
                    che in partita. Da qui un funnel completo: video manifesto,
                    sales page dedicata, performance marketing su Meta Ads e
                    seeding cross-canale tra i media owned Mizuno e la community
                    Problemi di Volley.
                  </p>
                </RevealOnScroll>
              </div>
            </div>
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll delay={0.25}>
                <div className="border border-dodgers/20 p-6 md:p-7" data-testid="scheda-progetto">
                  <span className="font-condensed uppercase tracking-widest text-xs opacity-60 block mb-5">
                    Scheda progetto
                  </span>
                  <dl>
                    <SchedaRow label="Progetto">
                      Reach Beyond — Lancio scarpa volley
                    </SchedaRow>
                    <SchedaRow label="Cliente">Mizuno</SchedaRow>
                    <SchedaRow label="Testimonial">
                      Gianluca Galassi · Yuri Romanò<br />
                      Francesco Recine · Leonardo Scanferla
                    </SchedaRow>
                    <SchedaRow label="Ruolo">
                      Concept · Gestione testimonial<br />
                      Produzione video · Sales page<br />
                      Meta Ads · Seeding cross-canale
                    </SchedaRow>
                    <SchedaRow label="Pilastro">
                      Business Impact · Content &amp; IP Studio<br />
                      Performance Marketing &amp; Growth
                    </SchedaRow>
                    <SchedaRow label="Anno">2023</SchedaRow>
                  </dl>
                </div>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 3 — LA SFIDA */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-sfida">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll>
                <SectionEyebrow>La sfida</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Lanciare un prodotto, non solo una campagna.
                </h2>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[64ch] opacity-90">
              <RevealOnScroll delay={0.15}>
                <p>
                  Il punto di partenza era una scarpa tecnica eccellente ma in
                  un mercato saturato dai grandi brand globali (Asics, Mizuno
                  stessa, Nike, Adidas). Una campagna di awareness pura —
                  testimonial + video + paid social di brand uplift — sarebbe
                  stata utile ma non avrebbe risposto al vero obiettivo del
                  lancio: vendere il prodotto a un pubblico italiano specifico,
                  in una finestra temporale precisa.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.22}>
                <p>
                  La sfida operativa era costruire un'attivazione che funzionasse
                  contemporaneamente come costruzione di percezione (la scarpa
                  associata a quattro Campioni del Mondo dentro un concept
                  riconoscibile) e come macchina di conversione diretta (sales
                  page + advertising performance + tracciamento). Due logiche
                  che il marketing sportivo italiano tiene tipicamente separate
                  — la prima delegata alla comunicazione, la seconda
                  all'e-commerce. Le abbiamo tenute insieme dentro un'unica
                  regia.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 4 — IL CONCEPT / VIDEO */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-concept">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10 md:mb-14">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>Reach Beyond</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Campioni in allenamento, prima che in partita.
                </h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[68ch] opacity-90">
                  Reach Beyond è il concept che abbiamo costruito attorno al
                  claim "Against all odds". L'idea: Mizuno sta con chi guarda
                  oltre, con chi si pone un obiettivo e ci mette tutto, perché
                  sa che il segreto per vincere non è la partita — è
                  l'allenamento. Preparation, reactivity, stability: le tre
                  qualità della scarpa diventano le tre dimensioni che separano
                  i vincenti dagli altri. Il video manifesto ha messo insieme i
                  quattro testimonial dentro questa narrazione, in un linguaggio
                  che è insieme istituzionale e contemporaneo.
                </p>
              </RevealOnScroll>
            </div>
          </div>

          <RevealOnScroll delay={0.05}>
            <div className="mx-auto max-w-[1100px]" data-testid="video-reach-beyond">
              <WistiaPlayer mediaId="ydc95b2azb" aspect={1.7777777777777777} />
              <p
                className="mt-4 text-[14px] leading-[1.55] text-mandrogni"
                style={{ fontStyle: "italic" }}
              >
                Reach Beyond — Il video manifesto con Galassi, Romanò, Recine e
                Scanferla.
              </p>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 5 — APPROCCIO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-approccio">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>Approccio</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Concept, content, conversion. In un'unica regia.
                </h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[68ch] opacity-90">
                  Abbiamo gestito l'intero progetto end-to-end, integrando in
                  un'unica filiera attività che tipicamente vengono distribuite
                  tra creative agency, talent agency, web agency e media agency.
                  Il vantaggio operativo: nessun handoff perso tra fornitori,
                  nessuna deviazione di messaggio tra creative e performance,
                  nessun ritardo di feedback loop tra ADV e contenuto.
                </p>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7">
            <RevealOnScroll delay={0.05}>
              <ApproccioBlock
                num="01"
                title="Concept & storytelling"
                body="Definizione del territorio narrativo Reach Beyond, costruzione del claim 'Against all odds', elaborazione dei tre attributi chiave del prodotto (preparation, reactivity, stability) come metafora del percorso dell'atleta. Coerenza tra messaggio brand globale Mizuno e voce specifica per il mercato italiano del volley."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <ApproccioBlock
                num="02"
                title="Testimonial management"
                body="Gestione operativa dei quattro testimonial Mizuno — Galassi, Romanò, Recine, Scanferla — dalla preparazione del set ai contratti immagine, dalla scheda creativa alle giornate di shooting. Coordinamento con i club di appartenenza e con la nazionale per la disponibilità nelle finestre utili."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.19}>
              <ApproccioBlock
                num="03"
                title="Produzione video & photo"
                body="Produzione integrale del video manifesto e dei photo asset di supporto. Direzione artistica, regia, post-produzione. Versioning multi-formato per i diversi touchpoint (video lungo per i canali brand, cut social verticale per i feed, formati per le ADV Meta)."
              />
            </RevealOnScroll>
            <RevealOnScroll delay={0.26}>
              <ApproccioBlock
                num="04"
                title="Sales page + performance"
                body="Sviluppo della sales page dedicata al lancio della scarpa: architettura, copy, integrazione con l'e-commerce Mizuno, ottimizzazione conversion. Pianificazione e attivazione delle campagne Meta Ads — prospecting e retargeting — con audience tarate sul pubblico volley italiano. Seeding parallelo sulla community Problemi di Volley e media mix con i canali owned Mizuno."
              />
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 6 — GALLERIA */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-gallery">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10 md:mb-14">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>Visual</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Lo shooting, il video, il prodotto.
                </h2>
              </RevealOnScroll>
              <RevealOnScroll delay={0.18}>
                <p className="mt-7 text-[1.0625rem] md:text-[1.125rem] leading-relaxed max-w-[60ch] opacity-90">
                  Una selezione del photo report dello shooting con i quattro
                  azzurri, integrata con i visual prodotti per la sales page e
                  le campagne paid.
                </p>
              </RevealOnScroll>
            </div>
          </div>

          {/* Griglia uniforme 4×3 desktop · 2×6 tablet · 1×12 mobile · aspect 1:1 · gap 8px */}
          <div
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2"
            data-testid="gallery-grid"
          >
            {Array.from({ length: GALLERY_SLOTS }).map((_, i) => {
              const photo = GALLERY_PHOTOS[i];

              if (!photo) {
                return (
                  <RevealOnScroll
                    key={i}
                    delay={Math.min(i, 8) * 0.03}
                  >
                    <div
                      className="block w-full relative bg-dodgers/[0.05] border border-lime border-dashed flex items-center justify-center"
                      style={{ aspectRatio: "1 / 1" }}
                      data-testid={`gallery-placeholder-${i}`}
                      aria-label={`Asset ${i + 1} di ${GALLERY_SLOTS} — in arrivo`}
                    >
                      <span
                        className="font-condensed uppercase tracking-widest text-galactico"
                        style={{ fontSize: "12px" }}
                      >
                        Foto in arrivo
                      </span>
                    </div>
                  </RevealOnScroll>
                );
              }

              return (
                <RevealOnScroll
                  key={i}
                  delay={Math.min(i, 8) * 0.03}
                >
                  <button
                    type="button"
                    onClick={() => setLightboxIndex(i)}
                    className="block w-full overflow-hidden relative group focus:outline-none bg-dodgers/[0.04]"
                    style={{ aspectRatio: "1 / 1" }}
                    data-testid={`gallery-${i}`}
                    data-hover="true"
                    aria-label={`Apri asset ${i + 1} di ${GALLERY_SLOTS}`}
                  >
                    <img
                      src={photo}
                      alt={LIGHTBOX_IMAGES[i].alt}
                      loading="lazy"
                      className="block w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.02]"
                      draggable="false"
                    />
                    <span
                      className="absolute inset-0 pointer-events-none border-[1.5px] border-lime opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-hidden="true"
                    />
                  </button>
                </RevealOnScroll>
              );
            })}
          </div>
        </div>
      </section>

      <Lightbox
        images={LIGHTBOX_IMAGES}
        openIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
      />

      {/* SEZ. 7 — IMPATTO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-impatto">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12 md:mb-16">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll>
                <SectionEyebrow>Impatto</SectionEyebrow>
              </RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Awareness e conversione, nello stesso funnel.
                </h2>
              </RevealOnScroll>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
            <RevealOnScroll delay={0.05}>
              <KpiBlock value="623.000+" label="Impressions di campagna" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.1}>
              <KpiBlock value="34.000+" label="Interazioni generate" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.15}>
              <KpiBlock value="4" label="Azzurri Campioni del Mondo testimonial" />
            </RevealOnScroll>
            <RevealOnScroll delay={0.2}>
              <KpiBlock value="1" label="Sales page + Meta Ads performance-driven" />
            </RevealOnScroll>
          </div>

          <p className="mt-8 text-[13px] leading-[1.55] text-mandrogni opacity-80 max-w-[80ch]">
            Dati aggregati della campagna paid e seeding nei canali Mizuno owned
            + Problemi di Volley. Engagement rate calcolato sul rapporto
            interazioni/impressions ai livelli di benchmark per campagne brand
            del segmento sportswear italiano.
          </p>

          <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            <RevealOnScroll delay={0.05}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-lime">
                  Concept riconoscibile
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[40ch]">
                  Reach Beyond non è stata una tagline appiccicata alla scarpa,
                  è stata un'identità narrativa coerente su tutti i touchpoint
                  — dal video manifesto agli annunci Meta, dalla sales page alle
                  stories sui canali dei testimonial. Quando un'audience
                  riconosce lo stesso codice in cinque posti diversi, il
                  messaggio si fissa.
                </p>
              </div>
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-lime">
                  Testimonial come asset, non endorsement
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[40ch]">
                  I quattro azzurri non sono apparsi solo nel video. Sono
                  entrati nella sales page, nelle campagne Meta, nei contenuti
                  seeding. Una gestione integrata del talent ha trasformato la
                  loro presenza in un asset commerciale, non in un'apparizione
                  di brand uplift.
                </p>
              </div>
            </RevealOnScroll>
            <RevealOnScroll delay={0.19}>
              <div>
                <h4 className="font-condensed uppercase tracking-widest text-sm md:text-[15px] mb-3 text-lime">
                  Full-funnel sotto una sola regia
                </h4>
                <p className="text-[15px] md:text-[1rem] leading-relaxed opacity-90 max-w-[40ch]">
                  Awareness (video + seeding), consideration (sales page +
                  content lungo), conversion (Meta Ads + retargeting). Un'unica
                  filiera ha tenuto insieme tre stadi del funnel che il
                  marketing sportivo italiano spesso tiene separati — perdendo
                  lungo la catena dati, messaggio e tempo.
                </p>
              </div>
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 8 — CREDITS */}
      <section className="py-20 md:py-28 bg-galactico text-dodgers" data-testid="section-credits">
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>Credits</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <div className="mt-8 max-w-[720px]">
              <dl>
                <SchedaRow label="Cliente">Mizuno</SchedaRow>
                <SchedaRow label="Progetto">
                  Reach Beyond — Lancio scarpa volley
                </SchedaRow>
                <SchedaRow label="Testimonial">
                  Gianluca Galassi · Yuri Romanò<br />
                  Francesco Recine · Leonardo Scanferla
                </SchedaRow>
                <SchedaRow label="Ruolo OnSports">
                  Concept &amp; storytelling · Talent management<br />
                  Produzione video · Sales page · Meta Ads<br />
                  Social seeding · Media mix
                </SchedaRow>
                <SchedaRow label="Servizi">
                  Content &amp; IP Studio · Performance Marketing &amp; Growth
                </SchedaRow>
                <SchedaRow label="Anno">2023</SchedaRow>
              </dl>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 9 — CTA FINALE */}
      <section
        className="bg-dodgers text-galactico py-24 md:py-40 border-t border-galactico/10"
        data-testid="section-cta-finale"
      >
        <div className="container-os">
          <RevealOnScroll>
            <h2 className="display-h2 max-w-[30ch]">
              Hai un prodotto da lanciare e una community da raggiungere?
              Costruiamo insieme il suo{" "}
              <span className="text-lime">IMPATTO</span>.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <p className="mt-8 body-lg opacity-85 max-w-[62ch]">
              Lavoriamo con brand sportswear, retailer e produttori che vogliono
              integrare narrazione e conversione in un'unica filiera. Concept,
              talent, content, sales page, paid media. Senza handoff persi tra
              fornitori.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.18}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contatti" data-testid="cta-parliamone">
                <Button variant="primary">Parliamone</Button>
              </Link>
              <Link to="/works" data-testid="cta-altri-casi">
                <Button variant="secondary-light">Vedi altri casi studio</Button>
              </Link>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 10 — NAVIGAZIONE PROGETTI */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-nav-progetti">
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>Naviga i progetti</SectionEyebrow>
          </RevealOnScroll>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to={prev.to || "/works"}
              className="group border border-dodgers/20 p-7 md:p-10 hover:border-dodgers transition-colors flex flex-col gap-4"
              data-hover="true"
              data-testid="nav-prev"
            >
              <span className="font-condensed uppercase tracking-widest text-xs opacity-70">
                ← Progetto precedente
              </span>
              <span className="font-display text-2xl md:text-3xl leading-tight group-hover:text-mandrogni transition-colors">
                {prev.title}
              </span>
              <span className="text-sm opacity-75">{prev.category}</span>
            </Link>
            <Link
              to={next.to || "/works"}
              className="group border border-dodgers/20 p-7 md:p-10 hover:border-dodgers transition-colors flex flex-col gap-4 md:text-right md:items-end"
              data-hover="true"
              data-testid="nav-next"
            >
              <span className="font-condensed uppercase tracking-widest text-xs opacity-70 inline-flex items-center gap-2">
                Progetto successivo <ArrowUpRight size={14} />
              </span>
              <span className="font-display text-2xl md:text-3xl leading-tight group-hover:text-mandrogni transition-colors">
                {next.title}
              </span>
              <span className="text-sm opacity-75">{next.category}</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default WorkMizunoReachBeyond;
